import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  Minus, 
  Mail, 
  Calendar, 
  Mic, 
  Users,
  ChevronRight,
  Sparkles,
  Clock,
  Target,
  Send,
  CalendarPlus,
  CheckSquare
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';

// ============================================================================
// TYPES
// ============================================================================

interface RelationshipData {
  rel_id: string;
  entity: {
    kind: string;
    display_name: string;
    role_title: string;
    timezone: string;
  };
  context: {
    type: string;
    tier: string;
    stage: string;
    objective: string;
    owner_person_id: string;
    owner_name: string;
    value_currency: string;
    value_amount: number;
    expected_cadence_days: number;
  };
  scores: {
    as_of_date: string;
    window: string;
    health: number;
    dimensions: Array<{
      name: string;
      value: number;
      delta: number;
      confidence: number;
      top_signals: string[];
      spark: number[];
    }>;
  };
  benchmarks: {
    cohort_name: string;
    delta_vs_p50: number;
    percentile: number;
  };
  drift: {
    value: number;
    trend: string;
    next_touch_due: string;
  };
  timeline: Array<{
    id: string;
    date: string;
    kind: string;
    title: string;
    participants: string[];
    sentiment: string;
    preview: string;
    actions?: string[];
    attachments?: string[];
  }>;
  voice_journal?: {
    transcript: string;
    sentiment: string;
    insights: string[];
    detected_actions: Array<{
      title: string;
      predicted_lift: number;
      confidence: number;
      action_type: string;
      payload?: Record<string, any>;
    }>;
    tags: string[];
    associated_contact: {
      display: string;
      email: string;
    };
  };
  actions: Array<{
    title: string;
    predicted_lift: number;
    confidence: number;
    action_type: string;
  }>;
  provenance: {
    last_sync: string;
    sample_size: number;
    confidence: number;
    dq_score: number;
  };
  tags: string[];
}

interface RelationshipGlassCardProps {
  data: RelationshipData;
  onAction?: (actionType: string, payload?: any) => void;
}

// ============================================================================
// STYLING TOKENS
// ============================================================================

const glassStyles = {
  card: "backdrop-blur-xl bg-white/60 dark:bg-gray-950/40 border border-white/20 dark:border-white/10 shadow-2xl",
  panel: "backdrop-blur-md bg-white/40 dark:bg-gray-900/30 border border-white/20 dark:border-white/10 rounded-lg",
  hover: "hover:bg-white/70 dark:hover:bg-gray-900/50 transition-all duration-300",
  glow: "shadow-[0_0_40px_-10px_rgba(251,207,88,0.3)]"
};

// ============================================================================
// METRIC TILE COMPONENT
// ============================================================================

const MetricTile = ({ 
  dimension, 
  isExpanded 
}: { 
  dimension: RelationshipData['scores']['dimensions'][0];
  isExpanded: boolean;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  
  const getTrendIcon = () => {
    if (dimension.delta > 0) return <TrendingUp className="h-3 w-3 text-green-500" />;
    if (dimension.delta < 0) return <TrendingDown className="h-3 w-3 text-red-500" />;
    return <Minus className="h-3 w-3 text-gray-400" />;
  };
  
  const getTrendColor = () => {
    if (dimension.delta > 0) return "text-green-600 dark:text-green-400";
    if (dimension.delta < 0) return "text-red-600 dark:text-red-400";
    return "text-gray-500";
  };
  
  return (
    <motion.div
      className={cn(glassStyles.panel, glassStyles.hover, "p-4 cursor-pointer relative overflow-hidden")}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
    >
      {/* Sparkline background */}
      <svg 
        className="absolute inset-0 w-full h-full opacity-10" 
        preserveAspectRatio="none"
        viewBox="0 0 100 100"
      >
        <polyline
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="text-primary"
          points={dimension.spark.map((v, i) => 
            `${(i / (dimension.spark.length - 1)) * 100},${100 - v}`
          ).join(' ')}
        />
      </svg>
      
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
            {dimension.name}
          </span>
          {getTrendIcon()}
        </div>
        
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold text-gray-900 dark:text-white">
            {dimension.value}
          </span>
          <span className={cn("text-sm font-medium", getTrendColor())}>
            {dimension.delta > 0 ? '+' : ''}{dimension.delta}
          </span>
        </div>
        
        <AnimatePresence>
          {isHovered && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="mt-3 space-y-1"
            >
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Confidence: {Math.round(dimension.confidence * 100)}%
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-300 space-y-0.5">
                {dimension.top_signals.map((signal, idx) => (
                  <div key={idx} className="flex items-start gap-1">
                    <span className="text-primary">•</span>
                    <span>{signal}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

// ============================================================================
// TIMELINE ITEM COMPONENT
// ============================================================================

const TimelineItem = ({ item }: { item: RelationshipData['timeline'][0] }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const getIcon = () => {
    switch (item.kind) {
      case 'email': return <Mail className="h-4 w-4" />;
      case 'meeting': return <Calendar className="h-4 w-4" />;
      case 'voice': return <Mic className="h-4 w-4" />;
      case 'intro': return <Users className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };
  
  const getSentimentColor = () => {
    switch (item.sentiment) {
      case 'positive': return 'text-green-500 bg-green-500/10';
      case 'negative': return 'text-red-500 bg-red-500/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };
  
  return (
    <motion.div
      className={cn(glassStyles.panel, glassStyles.hover, "p-3 cursor-pointer")}
      onClick={() => setIsExpanded(!isExpanded)}
      whileHover={{ x: 4 }}
      transition={{ duration: 0.2 }}
    >
      <div className="flex items-start gap-3">
        <div className={cn("p-2 rounded-lg", getSentimentColor())}>
          {getIcon()}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {item.title}
            </span>
            <Badge variant="outline" className="text-xs">
              {item.kind}
            </Badge>
          </div>
          
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
            {new Date(item.date).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit'
            })}
          </div>
          
          <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-1">
            {item.preview}
          </p>
          
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-2 space-y-2"
              >
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  <span className="font-medium">Participants: </span>
                  {item.participants.join(', ')}
                </div>
                
                {item.actions && item.actions.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {item.actions.map((action, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        {action}
                      </Badge>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

// ============================================================================
// MAIN CARD COMPONENT
// ============================================================================

export const RelationshipGlassCard = ({ data, onAction }: RelationshipGlassCardProps) => {
  const [showDataSpine, setShowDataSpine] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };
  
  const getHealthColor = (health: number) => {
    if (health >= 75) return 'text-green-600 dark:text-green-400';
    if (health >= 50) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };
  
  return (
    <div className="relative w-full max-w-6xl mx-auto p-6">
      <motion.div
        className={cn(glassStyles.card, glassStyles.glow, "rounded-2xl overflow-hidden")}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        {/* HEADER */}
        <div className={cn(glassStyles.panel, "p-6 border-b border-white/20 dark:border-white/10")}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {data.entity.display_name}
                </h2>
                <Badge variant="outline" className="text-xs">
                  {data.entity.kind}
                </Badge>
                <Badge variant="secondary" className="text-xs">
                  {data.context.stage}
                </Badge>
              </div>
              
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                {data.entity.role_title} • {data.context.type} • Tier {data.context.tier}
              </p>
              
              <div className="flex items-center gap-4 text-sm">
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Owner: </span>
                  <span className="text-gray-900 dark:text-white font-medium">
                    {data.context.owner_name}
                  </span>
                </div>
                <Separator orientation="vertical" className="h-4" />
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Value: </span>
                  <span className="text-gray-900 dark:text-white font-medium">
                    {formatCurrency(data.context.value_amount, data.context.value_currency)}
                  </span>
                </div>
              </div>
            </div>
            
            <motion.div
              className="text-right"
              whileHover={{ scale: 1.05 }}
            >
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                Health ({data.scores.window})
              </div>
              <div className={cn("text-4xl font-bold", getHealthColor(data.scores.health))}>
                {data.scores.health}
              </div>
              <AnimatePresence>
                {expandedSection === 'health' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="mt-2 text-xs text-gray-600 dark:text-gray-300"
                  >
                    <div>vs. {data.benchmarks.cohort_name}</div>
                    <div className="font-medium">
                      {data.benchmarks.delta_vs_p50 > 0 ? '+' : ''}{data.benchmarks.delta_vs_p50} 
                      {' '}(P{data.benchmarks.percentile})
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </div>
        
        {/* METRICS GRID */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {data.scores.dimensions.map((dimension) => (
              <MetricTile 
                key={dimension.name} 
                dimension={dimension}
                isExpanded={expandedSection === dimension.name}
              />
            ))}
          </div>
        </div>
        
        {/* TIMELINE & VOICE JOURNAL */}
        <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Timeline */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                Timeline
              </h3>
              <Badge variant="outline" className="text-xs">
                {data.timeline.length} items
              </Badge>
            </div>
            
            <div className="space-y-3 max-h-96 overflow-y-auto pr-2 custom-scrollbar">
              {data.timeline.map((item) => (
                <TimelineItem key={item.id} item={item} />
              ))}
            </div>
          </div>
          
          {/* Voice Journal */}
          {data.voice_journal && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  AI Voice Journal
                </h3>
                <Badge variant="outline" className="text-xs">
                  {data.voice_journal.sentiment}
                </Badge>
              </div>
              
              <div className={cn(glassStyles.panel, "p-4 space-y-4 max-h-96 overflow-y-auto custom-scrollbar")}>
                <div>
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                    Transcript
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    {data.voice_journal.transcript}
                  </p>
                </div>
                
                <Separator />
                
                <div>
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                    Insights
                  </div>
                  <ul className="space-y-1">
                    {data.voice_journal.insights.map((insight, idx) => (
                      <li key={idx} className="text-sm text-gray-700 dark:text-gray-300 flex items-start gap-2">
                        <span className="text-primary">•</span>
                        <span>{insight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <Separator />
                
                <div>
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3">
                    Detected Actions
                  </div>
                  <div className="space-y-2">
                    {data.voice_journal.detected_actions.map((action, idx) => (
                      <motion.div
                        key={idx}
                        className={cn(glassStyles.panel, glassStyles.hover, "p-3 flex items-center justify-between")}
                        whileHover={{ x: 4 }}
                      >
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                            {action.title}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                            <span>Lift: +{action.predicted_lift}</span>
                            <span>•</span>
                            <span>{Math.round(action.confidence * 100)}% confidence</span>
                          </div>
                        </div>
                        
                        <Button
                          size="sm"
                          variant="ghost"
                          className="ml-2"
                          onClick={() => onAction?.(action.action_type, action.payload)}
                        >
                          {action.action_type === 'schedule' && <CalendarPlus className="h-4 w-4" />}
                          {action.action_type === 'email' && <Send className="h-4 w-4" />}
                          {action.action_type === 'note' && <CheckSquare className="h-4 w-4" />}
                        </Button>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* SUGGESTED ACTIONS */}
        <div className="p-6 border-t border-white/20 dark:border-white/10">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            Suggested Actions
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {data.actions.map((action, idx) => (
              <motion.div
                key={idx}
                className={cn(glassStyles.panel, glassStyles.hover, "p-4 cursor-pointer")}
                whileHover={{ scale: 1.02 }}
                onClick={() => onAction?.(action.action_type)}
              >
                <div className="flex items-start justify-between mb-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {action.title}
                  </span>
                  <Badge variant="outline" className="text-xs">
                    +{action.predicted_lift}
                  </Badge>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {Math.round(action.confidence * 100)}% confidence
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
      
      {/* DATA SPINE (RIGHT EDGE) */}
      <motion.div
        className={cn(
          "fixed right-0 top-0 h-full w-1 bg-primary/20 cursor-pointer z-50",
          showDataSpine && "w-64"
        )}
        onHoverStart={() => setShowDataSpine(true)}
        onHoverEnd={() => setShowDataSpine(false)}
        transition={{ duration: 0.3 }}
      >
        <AnimatePresence>
          {showDataSpine && (
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
              className={cn(glassStyles.card, "absolute right-0 top-0 h-full w-64 p-6 space-y-4 overflow-y-auto")}
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Data Quality
              </h3>
              
              <div className="space-y-3 text-sm">
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Last Sync
                  </div>
                  <div className="text-gray-900 dark:text-white">
                    {new Date(data.provenance.last_sync).toLocaleString()}
                  </div>
                </div>
                
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Sample Size
                  </div>
                  <div className="text-gray-900 dark:text-white">
                    {data.provenance.sample_size} interactions
                  </div>
                </div>
                
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Confidence
                  </div>
                  <div className="text-gray-900 dark:text-white">
                    {Math.round(data.provenance.confidence * 100)}%
                  </div>
                </div>
                
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    DQ Score
                  </div>
                  <div className="text-gray-900 dark:text-white font-medium">
                    {data.provenance.dq_score}/100
                  </div>
                </div>
                
                <Separator />
                
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    Tags
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {data.tags.map((tag, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default RelationshipGlassCard;
